import { mountLogin } from './views/login.js';
import { mountLauncher } from './views/launcher.js';
import { mountChangePassword } from './views/changePassword.js';
import { mountAdministration } from './views/administration.js';
import { mountWebledgerAccounts } from './views/webledgerAccounts.js';
import { mountWebledgerTransactions } from './views/webledgerTransactions.js';
import { mountWebledgerTransactionHistory } from './views/webledgerTransactionHistory.js';
import { mountWebledgerRegister } from './views/webledgerRegister.js';
import { mountWebledgerReports } from './views/webledgerReports.js';
import { mountWebledgerWallets, readSection } from './views/webledgerWallets.js';
import { mountWebledgerSettings } from './views/webledgerSettings.js';
import { mountWebledgerBankSync } from './views/webledgerBankSync.js';
import { mountWebledgerBackup } from './views/webledgerBackup.js';
import { mountWebledgerDeleted } from './views/webledgerDeleted.js';
import { appHref, globals } from './globals.js';
import { getStoredSession } from './auth-storage.js';
import { applyTheme, watchSystemTheme } from './theme.js';
import { placeToResume, rememberPlace } from './lastPlace.js';
import { registerServiceWorker } from './serviceWorker.js';
import { disableNumberStepping } from '@colplat/ui-web';

/**
 * Login, launcher, and every app's real client, in one bundle — navigation
 * between them is a plain full page load (an `<a href>`, not a client-side
 * router), so `services/api`'s SPA-fallback static serving (any
 * extension-less path -> this one index.html) already covers routing.
 */
function currentPath(): string {
  const base = globals().base;
  const pathname = window.location.pathname;
  return base && pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
}

function main(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('missing #app root element');

  // Before any routing: the offline shell must be installed on every page of
  // the app, including /login, or a reload offline lands on the browser's
  // error page (ADR-0022). Registration itself is deferred to `load`.
  registerServiceWorker();
  // The arrow keys never change an amount (noSpin.ts).
  disableNumberStepping();

  /*
   * Before routing, so the first paint is already the right colour. A page
   * that renders light and then flips is worse than one that was always
   * light, especially at night.
   */
  const session = getStoredSession();
  applyTheme(session?.userId);
  watchSystemTheme(session?.userId);

  const path = currentPath();

  if (path === '/login') {
    const signedIn = getStoredSession();
    if (signedIn) {
      // Back where they were, not to the apps list. Someone who reloads a
      // page mid-task, or whose session was restored, is still in the middle
      // of whatever they were doing.
      window.location.assign(placeToResume(signedIn.userId));
      return;
    }
    mountLogin(root);
    return;
  }

  if (!getStoredSession()) {
    window.location.assign(appHref('/login'));
    return;
  }

  /*
   * Where we are now is where to come back to.
   *
   * Recorded on arrival rather than on leaving: there is no reliable moment
   * of leaving — a closed laptop, a killed tab and a crashed browser all
   * look the same from in here — whereas every page that opens announces
   * itself by existing.
   *
   * The query string rides along, because on these pages it is the view: a
   * register is an account id, a filtered report is its filters.
   */
  rememberPlace(session?.userId, path + window.location.search);

  if (path === '/change-password') {
    mountChangePassword(root);
    return;
  }
  if (path.startsWith('/administration')) {
    void mountAdministration(root);
    return;
  }
  // Before the list route, which would otherwise swallow it.
  const historyMatch = /^\/webledger\/transactions\/([^/]+)\/history$/.exec(path);
  if (historyMatch) {
    void mountWebledgerTransactionHistory(root, decodeURIComponent(historyMatch[1]!));
    return;
  }
  if (path.startsWith('/webledger/transactions')) {
    void mountWebledgerTransactions(root);
    return;
  }
  // Before the accounts route, which would otherwise swallow it.
  const registerMatch = /^\/webledger\/accounts\/([^/]+)\/register$/.exec(path);
  if (registerMatch) {
    void mountWebledgerRegister(root, decodeURIComponent(registerMatch[1]!));
    return;
  }
  /*
   * The wallet sections are routes now, not tabs behind a preference.
   *
   * They sit in the top nav, and a nav item that cannot be linked to is not
   * a nav item — it also means the back button, a bookmark and a shared link
   * all land where they say they will, none of which was true while the
   * section lived in `prefs`.
   */
  const walletsMatch = /^\/webledger\/wallets(?:\/(log|budgets|between))?\/?$/.exec(path);
  if (walletsMatch) {
    const sub = walletsMatch[1];
    void mountWebledgerWallets(root, sub === 'between' ? 'debts' : readSection(sub));
    return;
  }
  if (path.startsWith('/webledger/wallets')) {
    void mountWebledgerWallets(root);
    return;
  }

  if (path.startsWith('/webledger/reports')) {
    void mountWebledgerReports(root);
    return;
  }
  if (path.startsWith('/webledger/deleted')) {
    void mountWebledgerDeleted(root);
    return;
  }
  if (path.startsWith('/webledger/backup')) {
    void mountWebledgerBackup(root);
    return;
  }
  if (path.startsWith('/webledger/settings')) {
    void mountWebledgerSettings(root);
    return;
  }
  // `startsWith`, so the bank's own redirect target — /webledger/bank/callback
  // with `code` and `state` in the query — lands on the same page, which is
  // what finishes the consent. See EB_REDIRECT_URL in ops/env.
  if (path.startsWith('/webledger/bank')) {
    void mountWebledgerBankSync(root);
    return;
  }
  if (path.startsWith('/webledger')) {
    void mountWebledgerAccounts(root);
    return;
  }
  void mountLauncher(root);
}

main();
